import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {Row, Col} from 'antd';
import { useTranslation } from 'react-i18next';

// Types
import { CategoryType, CategoryParamsType } from '../../types/CategoryTypes';
import { FilterType, FiltersPaginationType, PaginationType } from '../../types/SharedTypes';

// Store
import { getParams, getAll, upload, remove } from "../../store/redux/actions/categoryActions";
import * as categorySelectors from '../../store/redux/selectors/categorySelectors';
import {useAppDispatch} from '../../store/store';

// Components and Subcomponents
import TitleComponent from '../subComponents/TitleComponent';
import VerticalFilterComponent from '../subComponents/VerticalFilterComponent';
import TableComponent from '../subComponents/TableComponent';
import CategoriesTopButtons from './CategoriesTopButtons';
import UploadExcelModal from '../subComponents/UploadExcelModal';
import CategoryModal from './CategoryModal';

// Utils
import { useScreenSizeValidator } from '../../utils/generalUtils';
import tableColumns from '../../utils/tableColumns';


const Categories = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const categoryParams = useSelector(categorySelectors.categoryParamsSelector)!;
  const response = useSelector(categorySelectors.allCategoriesSelector)!;
  const [paginationData, setPaginationData] = useState<FiltersPaginationType>({
    paginationVm: {
      page: 1,
      size: 10,
    },
    filterVm: {}
  });
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<CategoryType | undefined>(undefined);
  const [showUploadCategoryModal, setShowUploadCategoryModal] = useState(false);
  const [filters, setFilters] = useState<FilterType[]>([
    { name: "models", label: `${t('model')}:`, value: undefined, fieldType: 'input' },
    { name: "primaryCategories", label: `${t('category')}:`, value: undefined, fieldType: 'select',
      items: { list: [], idIdentifier: "id", valueIdentifier: "value" }, mode: "multiple" },
  ]);
  const isSmallScreen = useScreenSizeValidator('(max-width: 576px)');
  
  useEffect(() => {
    dispatch(getParams(paginationData));
  }, [paginationData]);

  useEffect(() => {
    let newFilters = filters;
    Object.keys(categoryParams).map(key => {
      const filterFound = newFilters.find(filter => filter.name === key)!;
      filterFound.items!.list = categoryParams[key as keyof CategoryParamsType];
    });
    setFilters(newFilters);
  }, [categoryParams]);

  const handleRemoveCategory = (categoryId: string) => {
    dispatch(remove(categoryId, paginationData));
  };

  const handleChangePagination = (pagination: PaginationType) => {
    const newPagination = paginationData;
    newPagination.paginationVm.page = pagination.page;
    newPagination.paginationVm.size = pagination.size;
    setPaginationData(newPagination);
    dispatch(getAll(paginationData));
  };

  return (
    <Row>
      <Col md={14} sm={14} xs={24}>
        <TitleComponent level={3} text={t('categories')} className={'font-semibold text-gray-900'} />
      </Col>

      <Col md={24} xs={24} className={'text-left'}>
        <VerticalFilterComponent filters={filters}
                                 paginationData={paginationData}
                                 setPaginationData={setPaginationData}
                                 additionalContent={
                                   <CategoriesTopButtons isSmallScreen={isSmallScreen}
                                                         onToggleUploadCategoryModal={() => setShowUploadCategoryModal(true)}
                                                         onToggleCategoryModal={() => {
                                                           setCategoryModalVisible(true);
                                                           setCategoryToEdit(undefined);
                                                         }}
                                   />
                                 }
          />
      </Col>
      <Col md={24} xs={24} className={'mt-4'}>
        <TableComponent pageSize={paginationData.paginationVm.size}
                        currentPage={paginationData.paginationVm.page} total={response.count}
                        dataSource={response.rows} scrollX={500} rowKey={'model'}
                        columns={
                          tableColumns('category',
                            (category: CategoryType) => {
                              setCategoryToEdit(category);
                              setCategoryModalVisible(true);
                            },
                            (categoryId: string) => {
                              handleRemoveCategory(categoryId)
                            }
                          )
                        }
                        onChangePage={handleChangePagination}
        />
      </Col>

      {showUploadCategoryModal &&
        <UploadExcelModal title={t('loadCategories')}
                          onToggleModal={() => setShowUploadCategoryModal(false)}
                          upload={(formData) => dispatch(upload(formData, paginationData))}
        />
      }

      {categoryModalVisible && 
        <CategoryModal categoryToEdit={categoryToEdit}
                      paginationData={paginationData} 
                      onToggleModal={setCategoryModalVisible}/>
      }

    </Row>
  );
}

export default Categories;